import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { X, Search } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useResponsive } from '@/hooks/useResponsive';
import { usePageBar } from '@/components/layout/page-bar';
import { cn } from '@/lib/utils';

import ConnectionsList from '@/components/connectors/ConnectionsList';
import { SourcePicker } from '@/components/connectors/SourcePicker';
import { DynamicConnectionForm } from '@/components/connectors/DynamicConnectionForm';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { ConnectionUsageInstructionsModal } from '@/components/connectors/ConnectionUsageInstructionsModal';

import { useAuth } from '@/contexts/AuthContext';
import { apiClient, createAuthHeaders } from '@/lib/api-client';
import { openOAuthPopup } from '@/lib/connector-oauth';

import type {
  Connection,
  Connector,
  ConnectorListItem,
  AuthMethod,
  UIField,
  ConnectionAccessResponse,
} from '@optidev/shared-types';
import { UI_FIELD_TYPES } from '@optidev/shared-types';

export default function ConnectorsFull() {
  /*
   * `searchParams` is only read for the deep-link flow below (`?connector=&reqKey=`). There is no
   * `?tab=` any more: connections and the catalogue are one page, so nothing selects between them.
   */
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const { isMobile } = useResponsive();

  // Connectors state (fetched from API)
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  /*
   * There is no edit path. A connection is credentials plus a name; changing either in place is
   * indistinguishable from creating the right one and deleting the wrong one, and the in-place
   * version has to special-case masked secrets, OAuth re-consent and resource re-selection.
   * Rotate a key by connecting again and deleting the old connection; refresh an OAuth token
   * from the card menu.
   */
  // OAuth + Resource Picker state (two-phase flow)
  const [oauthAccessToken, setOAuthAccessToken] = useState<string | null>(null);
  const [oauthConnectionId, setOAuthConnectionId] = useState<string | null>(null);

  // Pending requirement key for auto-selecting connection when opened from Remix modal
  const [pendingReqKey, setPendingReqKey] = useState<string | null>(null);

  // Helper to restore body scroll when modal closes
  const restoreBodyScroll = () => {
    const scrollY = document.body.dataset.scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY, 10));
      delete document.body.dataset.scrollY;
    }
  };

  // Notify opener window and close tab if opened from another page (e.g., Remix modal)
  const notifyOpenerAndClose = (connectionId: string): boolean => {
    if (!pendingReqKey) return false;
    const opener = window.opener as Window & { onConnectionCreated?: (connectionId: string, reqKey: string) => void } | null;
    if (opener?.onConnectionCreated) {
      opener.onConnectionCreated(connectionId, pendingReqKey);
      setPendingReqKey(null); // Clear after use
      window.close();
      return true; // Tab will close, skip navigation
    }
    return false; // Normal flow, continue with navigation
  };

  // Usage modal state
  const [usageConnection, setUsageConnection] = useState<Connection | null>(null);

  // Connections state
  const [activeConnections, setActiveConnections] = useState<Connection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  /*
   * Name/type search over your own connections. The category and status filters that used to sit
   * beside it are gone with the tab that held them — over a single sideways row of cards there was
   * nothing left for them to narrow.
   */
  const [connectionSearch, setConnectionSearch] = useState('');

  // Load connectors on mount (needed for icons in both tabs)
  useEffect(() => {
    loadConnectors();
  }, []);

  // Auto-open connector modal if 'connector' param is in URL
  // Used when redirecting from other pages (e.g., Remix modal "Create new connection")
  useEffect(() => {
    const connectorId = searchParams.get('connector');
    const reqKey = searchParams.get('reqKey');
    if (connectorId && connectors.length > 0 && !showConnectionModal) {
      // Check if connector exists
      const connectorExists = connectors.some(c => c.id === connectorId);
      if (connectorExists) {
        // Store reqKey for later use when connection is created (for auto-selecting the right dropdown)
        if (reqKey) {
          setPendingReqKey(reqKey);
        }
        // Clear the params from URL to prevent re-opening on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('connector');
        newParams.delete('reqKey');
        navigate(`/dashboard/connectors?${newParams.toString()}`, { replace: true });
        // Open the modal
        handleConnect(connectorId);
      }
    }
  }, [connectors, searchParams]);

  // Connections and the catalogue are both on screen, so this runs once on mount.
  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load connectors from API
  const loadConnectors = async (): Promise<void> => {
    setLoadingConnectors(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const { data } = await apiClient.get<ConnectorListItem[]>(
        '/api/connectors',
        { headers: createAuthHeaders(token) }
      );
      setConnectors(data || []);
    } catch (error) {
      console.error('Failed to load connectors:', error);
      toast.error('Failed to load connectors');
    } finally {
      setLoadingConnectors(false);
    }
  };

  const loadConnections = async (): Promise<void> => {
    setLoadingConnections(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const { data } = await apiClient.get<Connection[]>('/api/connections', {
        headers: createAuthHeaders(token),
      });
      setActiveConnections(data || []);
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoadingConnections(false);
    }
  };

  /**
   * Only the count is consumed now (the page bar). Filtering, featured ordering and the
   * available/coming-soon split all moved into SourcePicker, which owns the catalogue.
   */
  const availableConnectors = useMemo(
    () => connectors.filter((c) => c.status === 'available'),
    [connectors],
  );

  // Filtered connections
  const filteredConnections = useMemo(() => {
    const search = connectionSearch.trim().toLowerCase();
    if (!search) return activeConnections;
    return activeConnections.filter(
      (connection) =>
        connection.name.toLowerCase().includes(search) ||
        (connection.type?.toLowerCase().includes(search) ?? false),
    );
  }, [activeConnections, connectionSearch]);

  // Check if connector has resource picker in its OAuth auth method
  const connectorHasResourcePicker = useMemo(() => {
    if (!selectedConnector?.uiSchema?.authMethods) return false;
    const oauthMethod = selectedConnector.uiSchema.authMethods.find(
      (m: AuthMethod) => m.id === 'oauth' || m.id === 'oauth2'
    );
    if (!oauthMethod) return false;
    // Check if has resource picker field or resourceScope is 'selected'
    const hasPickerField = oauthMethod.fields?.some(
      (f: UIField) => f.type === UI_FIELD_TYPES.RESOURCE_PICKER
    );
    return hasPickerField || oauthMethod.resourceScope === 'selected';
  }, [selectedConnector]);

  // Handle connector card click - fetch full details and show modal
  const handleConnect = async (connectorId: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) return;

      // Fetch full connector details (includes uiSchema)
      const { data } = await apiClient.get<Connector>(
        `/api/connectors/${connectorId}`,
        { headers: createAuthHeaders(token) }
      );
      setSelectedConnector(data);
      setShowConnectionModal(true);
    } catch (error) {
      console.error('Failed to load connector details:', error);
      toast.error('Failed to load connector');
    }
  };

  // Handle connection creation from DynamicConnectionForm
  const handleCreateConnection = async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }): Promise<void> => {
    setIsCreating(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      const { data: createdConnection } = await apiClient.post<Connection>('/api/connections', {
        type: selectedConnector!.id,
        ...formData,
      }, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection created successfully');
      restoreBodyScroll();
      setShowConnectionModal(false);
      setSelectedConnector(null);
      // Refresh connections list then navigate to connections tab
      await loadConnections().catch(console.error);
      if (!notifyOpenerAndClose(createdConnection.id)) {
        navigate('/dashboard/connectors');
      }
    } catch (error: unknown) {
      console.error('Failed to create connection:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to create connection');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle OAuth start for connectors that use OAuth (opens in popup)
  const handleOAuthStart = async (authMethod: string, connectionName?: string, additionalParams?: Record<string, string>): Promise<void> => {
    if (!selectedConnector) return;

    try {
      const token = await getIdToken();
      if (!token) return;

      // Get auth URL from backend (pass authMethod for scope overrides)
      const { data } = await apiClient.post<{ authUrl: string }>(
        `/api/connections/oauth/init`,
        {
          connectorId: selectedConnector.id,
          authMethod, // Pass the selected auth method (e.g., 'oauth', 'oauth_readonly')
          connectionName, // Pass user-typed name so OAuth connection uses it instead of auto-generated
          additionalParams, // Pre-OAuth fields (e.g., shopDomain for Shopify)
        },
        { headers: createAuthHeaders(token) }
      );

      if (data.authUrl) {
        // Open OAuth in popup
        const result = await openOAuthPopup(data.authUrl);

        if (result.success && result.connectionId) {
          // Check if connector has resource picker
          if (connectorHasResourcePicker) {
            // Two-phase flow: OAuth done, now show picker
            try {
              // Fetch access token from the new connection
              const { data: accessData } = await apiClient.post<ConnectionAccessResponse>(
                `/api/connections/${result.connectionId}/access`,
                {},
                { headers: createAuthHeaders(token) }
              );

              if (accessData.data?.accessToken) {
                // Store token and connection ID for the picker
                setOAuthAccessToken(accessData.data.accessToken as string);
                setOAuthConnectionId(result.connectionId);
                toast.success('Connected! Now select your files.');
                // Modal stays open, form will show the picker
              } else {
                // No access token returned, fall back to normal flow
                toast.success(`${result.connectorName || selectedConnector.name} connected successfully`);
                restoreBodyScroll();
                setShowConnectionModal(false);
                setSelectedConnector(null);
                await loadConnections().catch(console.error);
                if (!notifyOpenerAndClose(result.connectionId)) {
                  navigate('/dashboard/connectors');
                }
              }
            } catch (accessError) {
              console.error('Failed to get access token:', accessError);
              toast.success(`${result.connectorName || selectedConnector.name} connected (picker unavailable)`);
              restoreBodyScroll();
              setShowConnectionModal(false);
              setSelectedConnector(null);
              await loadConnections().catch(console.error);
              if (!notifyOpenerAndClose(result.connectionId)) {
                navigate('/dashboard/connectors');
              }
            }
          } else {
            // No resource picker - normal flow
            toast.success(`${result.connectorName || selectedConnector.name} connected successfully`);
            restoreBodyScroll();
            setShowConnectionModal(false);
            setSelectedConnector(null);
            await loadConnections().catch(console.error);
            if (!notifyOpenerAndClose(result.connectionId)) {
              navigate('/dashboard/connectors');
            }
          }
        } else if (result.error && result.error !== 'Authentication cancelled') {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('OAuth init error:', error);
      toast.error('Failed to start OAuth flow');
    }
  };

  // Handle update connection with selected resources (after OAuth + picker)
  const handleUpdateConnectionWithResources = async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }): Promise<void> => {
    if (!oauthConnectionId) return;

    setIsCreating(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      // Update the existing connection with selected resources in metadata (not credentials)
      // selectedResources is display data, not sensitive - store in metadata for faster access
      // Don't send name - preserve the auto-generated name from OAuth (e.g., "Google Sheets (email@example.com)")
      await apiClient.patch(`/api/connections/${oauthConnectionId}`, {
        metadata: {
          selectedResources: formData.credentials.selectedResources,
        },
      }, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection saved with selected files');
      restoreBodyScroll();
      setShowConnectionModal(false);
      const connectionId = oauthConnectionId;
      setSelectedConnector(null);
      setOAuthAccessToken(null);
      setOAuthConnectionId(null);
      // Refresh connections list then navigate to connections tab
      await loadConnections().catch(console.error);
      if (!notifyOpenerAndClose(connectionId)) {
        navigate('/dashboard/connectors');
      }
    } catch (error: unknown) {
      console.error('Failed to update connection:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to save connection');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle refresh OAuth token
  const handleRefreshToken = async (id: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.post(`/api/connections/${id}/refresh`, {}, {
        headers: createAuthHeaders(token),
      });

      toast.success('Token refreshed successfully');
      loadConnections();
    } catch (error) {
      console.error('Refresh token error:', error);
      toast.error('Failed to refresh token');
    }
  };

  // Handle toggle enabled
  const handleToggleEnabled = async (id: string, enabled: boolean): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.patch(`/api/connections/${id}`, { enabled }, {
        headers: createAuthHeaders(token),
      });

      loadConnections();
    } catch (error) {
      console.error('Toggle enabled error:', error);
      throw error; // Let the caller handle the error toast
    }
  };

  // Handle view usage (show code examples modal)
  const handleViewUsage = (connection: Connection): void => {
    setUsageConnection(connection);
  };

  // Handle delete connection
  const handleDeleteConnection = async (id: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.delete(`/api/connections/${id}`, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection deleted successfully');
      loadConnections();
    } catch (error) {
      console.error('Delete connection error:', error);
      toast.error('Failed to delete connection');
    }
  };

  // Handle test connection
  const handleTestConnection = async (id: string): Promise<void> => {
    const toastId = toast.loading('Testing connection...');

    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required', { id: toastId });
        return;
      }

      const { data } = await apiClient.post<{ success: boolean; message?: string }>(
        `/api/connections/${id}/test`,
        {},
        { headers: createAuthHeaders(token) }
      );

      if (data.success) {
        toast.success(data.message || 'Connection test successful!', { id: toastId });
      } else {
        toast.error(data.message || 'Connection test failed', { id: toastId });
      }
      loadConnections();
    } catch (error) {
      console.error('Test connection error:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Connection test failed', { id: toastId });
    }
  };

  /** Bumped to send focus to the picker's search box, so the empty state leads somewhere. */
  const [pickerFocus, setPickerFocus] = useState(0);

  /** Which connectors already have a connection, driving "Connected" in the picker. */
  const connectedConnectorIds = useMemo(
    () => new Set(activeConnections.map((c) => c.connectorId).filter(Boolean) as string[]),
    [activeConnections],
  );

  usePageBar({
    title: 'Connectors',
    description: 'Connect external data sources to power your apps and dashboards.',
    meta: loadingConnectors ? undefined : `${availableConnectors.length} available`,
  });

  return (
    <div className="flex min-h-full flex-col sm:h-full sm:min-h-0 sm:overflow-hidden">
      <div className={cn(
        "flex w-full flex-1 flex-col sm:min-h-0",
        isMobile ? "px-4 py-6" : "px-6 sm:px-8 lg:px-10 pt-6 pb-8"
      )}>
        {/* Title + description live in the shell page header (usePageBar) */}

        {/*
          Connections first, then a bounded picker.

          The catalogue used to be a tab rendering one bordered card per category, each wrapping a
          3-column grid. With 41 connectors across 17 categories, nine holding exactly one, that
          was 17 nested containers and a very long page, with the connections you actually own
          hidden behind the other tab. Now what you own is the page, and adding a source is a
          fixed-height panel underneath that scrolls internally.
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {/*
            Your connections take exactly the height their cards need — a fixed 30% share left a
            band of dead space above the picker whenever you had only a couple. Past the cap the
            list scrolls inside itself, and the picker always gets the rest of the page.
          */}
          <section className="flex min-h-0 flex-none flex-col">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <h2 className="text-sm font-semibold tracking-tight">
                Your connections
                {activeConnections.length > 0 && (
                  <span className="ml-2 font-normal tabular-nums text-muted-foreground">
                    {activeConnections.length}
                  </span>
                )}
              </h2>
              {activeConnections.length > 3 && (
                <div className="relative ml-auto w-full max-w-[240px]">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search connections"
                    value={connectionSearch}
                    onChange={(e) => setConnectionSearch(e.target.value)}
                    className="h-8 pl-8 text-xs"
                  />
                </div>
              )}
            </div>

            {loadingConnections ? (
              <div className="rounded-xl border border-border bg-card py-10 text-center">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : activeConnections.length === 0 ? (
              /* Roughly one card row tall, so the picker below still owns most of the page. */
              <button
                type="button"
                onClick={() => setPickerFocus((n) => n + 1)}
                className="group flex min-h-[152px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border transition-colors hover:border-primary/40 hover:bg-accent/30"
              >
                <span className="grid size-10 place-items-center rounded-full border border-dashed border-border text-xl leading-none text-muted-foreground transition-colors group-hover:border-primary/40 group-hover:text-primary">
                  +
                </span>
                <span className="text-sm font-semibold">Connect your first data source</span>
                <span className="text-xs text-muted-foreground">
                  Pick one below and your apps can use its data
                </span>
              </button>
            ) : filteredConnections.length === 0 ? (
              <div className="rounded-xl border border-border bg-card px-6 py-8 text-center">
                <p className="text-sm text-muted-foreground">No connections match that search.</p>
              </div>
            ) : (
              /* No height cap and no vertical scroll: the list is one sideways-scrolling row. */
              <ConnectionsList
                connections={filteredConnections}
                connectors={connectors}
                onDelete={handleDeleteConnection}
                onTest={handleTestConnection}
                onRefreshToken={handleRefreshToken}
                onToggleEnabled={handleToggleEnabled}
                onViewUsage={handleViewUsage}
              />
            )}
          </section>

          <SourcePicker
            className="min-h-0 flex-1"
            focusSearchSignal={pickerFocus}
            connectors={connectors}
            connectedIds={connectedConnectorIds}
            onPick={handleConnect}
            isLoading={loadingConnectors}
          />
        </div>

        {/* Connection setup modal — create only; see the note on the absent edit path above */}
        {showConnectionModal && selectedConnector && createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-hidden"
            ref={(el) => {
              // Lock body scroll and position when modal opens
              // This prevents Google Picker from calculating wrong height
              if (el) {
                const scrollY = window.scrollY;
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';
                document.body.style.overflow = 'hidden';
                // Store scroll position for restoration
                document.body.dataset.scrollY = String(scrollY);
              }
            }}
          >
            <div
              className="absolute inset-0"
              onClick={() => {
                restoreBodyScroll();
                setShowConnectionModal(false);
                setSelectedConnector(null);
                setOAuthAccessToken(null);
                setOAuthConnectionId(null);
              }}
            />
            {/*
              Flex column, not a scrolling box: the form owns a fixed header, a scrolling field
              area and a sticky footer, so Connect never scrolls out of reach on long forms.
            */}
            <div className={cn(
              "relative flex flex-col overflow-hidden bg-card shadow-xl w-full",
              isMobile
                ? "max-w-none max-h-[85dvh] rounded-t-2xl mt-auto"
                : "max-w-lg max-h-[90vh] rounded-lg"
            )}>
              <button
                onClick={() => {
                  restoreBodyScroll();
                  setShowConnectionModal(false);
                  setSelectedConnector(null);
                  setOAuthAccessToken(null);
                  setOAuthConnectionId(null);
                }}
                className="absolute right-4 top-3.5 p-1.5 hover:bg-accent rounded-lg transition-colors z-10"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
                <DynamicConnectionForm
                  key={oauthConnectionId || 'new'}
                  connector={selectedConnector}
                  onSubmit={oauthConnectionId ? handleUpdateConnectionWithResources : handleCreateConnection}
                  onCancel={() => {
                    restoreBodyScroll();
                    setShowConnectionModal(false);
                    setSelectedConnector(null);
                    setOAuthAccessToken(null);
                    setOAuthConnectionId(null);
                  }}
                  onOAuthStart={handleOAuthStart}
                  isSubmitting={isCreating}
                  accessToken={oauthAccessToken ?? undefined}
                  connectionId={oauthConnectionId ?? undefined}
                  getAuthToken={getIdToken}
                />
            </div>
          </div>,
          document.body
        )}

        {/* Connection Usage Instructions Modal */}
        <ConnectionUsageInstructionsModal
          connection={usageConnection}
          connector={usageConnection ? connectors.find(c => c.id === usageConnection.type) : undefined}
          isOpen={!!usageConnection}
          onClose={() => setUsageConnection(null)}
        />
      </div>
    </div>
  );
}
